import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useConversations } from '../../pages/EOS/Messages/useConversations';
import { ConversationSidebar } from '../../pages/EOS/Messages/components/ConversationSidebar';
import { NewConversationModal } from '../../pages/EOS/Messages/components/NewConversationModal';
import { AddMembersModal } from '../../pages/EOS/Messages/components/AddMembersModal';
import { ChatHeader } from '../../pages/EOS/Messages/components/ChatHeader';
import GroupChat from '../Client/GroupChat';
import { COLLECTION_NAMES } from '../../pages/EOS/types';

interface MessagesPopoverProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  children: React.ReactNode;
  unreadCount?: number;
}

export function MessagesPopover({ isOpen, onOpen, onClose, children }: MessagesPopoverProps) {
  const { currentUser } = useAuth();
  const {
    conversations,
    createDirectMessage,
    createGroupChat,
    addMembersToConversation,
    markAsRead,
  } = useConversations(currentUser?.uid);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | undefined
  >();
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');

  // Get selected conversation details
  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId
  );

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversationId) {
      markAsRead(selectedConversationId);
    }
  }, [selectedConversationId, markAsRead]);

  // Update conversation's lastMessageAt when new messages are added
  useEffect(() => {
    if (!selectedConversationId) return;

    const messagesQuery = query(
      collection(
        db,
        COLLECTION_NAMES.CONVERSATIONS,
        selectedConversationId,
        COLLECTION_NAMES.CHAT_MESSAGES
      ),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      async (snapshot) => {
        if (!snapshot.empty) {
          const lastMessage = snapshot.docs[0];
          const lastMessageData = lastMessage.data();
          const messageTimestamp = lastMessageData.timestamp;

          const conversationRef = doc(db, COLLECTION_NAMES.CONVERSATIONS, selectedConversationId);
          await updateDoc(conversationRef, {
            lastMessageAt: messageTimestamp || serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      },
      (error) => {
        console.error('Error listening to messages for conversation update:', error);
      }
    );

    return () => unsubscribe();
  }, [selectedConversationId]);

  const handleNewConversation = () => {
    setShowNewConversationModal(true);
  };

  const handleConversationCreated = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowNewConversationModal(false);
  };

  const handleMembersAdded = (conversationId: string) => {
    if (conversationId !== selectedConversationId) {
      setSelectedConversationId(conversationId);
    }
    setShowAddMembersModal(false);
  };

  const getConversationName = () => {
    if (!selectedConversation) return 'Messages';
    if (selectedConversation.type === 'direct') {
      return selectedConversation.otherUser?.name || 'Direct Message';
    }
    return selectedConversation.name || 'Group Chat';
  };

  // Calculate total unread count
  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  // Wrap children in a Box to add unread badge
  const childrenWithBadge = (
    <Box position="relative" display="inline-block">
      {children}
      {totalUnread > 0 && (
        <Box
          as="span"
          color="white"
          position="absolute"
          top="6px"
          right="4px"
          fontSize="0.8rem"
          minWidth="16px"
          height="18px"
          bgColor="#FB404B"
          borderRadius="3xl"
          zIndex={2}
          p="1px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {totalUnread > 99 ? '99+' : totalUnread}
        </Box>
      )}
    </Box>
  );

  // Add CSS to ensure popover portal has high z-index
  useEffect(() => {
    if (isOpen) {
      // Find and update all popover portal containers
      const updateZIndex = () => {
        // Chakra UI creates a portal, find it by looking for elements with popover-related attributes
        const portals = document.querySelectorAll('[data-popover], [role="dialog"], [id^="popover"]');
        portals.forEach((portal) => {
          const element = portal as HTMLElement;
          if (element.style) {
            element.style.zIndex = '1500';
          }
        });
        
        // Also try to find by Chakra UI's internal structure
        const chakraPortals = document.querySelectorAll('[data-chakra-portal]');
        chakraPortals.forEach((portal) => {
          const element = portal as HTMLElement;
          if (element.style) {
            element.style.zIndex = '1500';
          }
        });
      };

      // Add global CSS rule
      const style = document.createElement('style');
      style.id = 'messages-popover-zindex-fix';
      style.textContent = `
        [data-popover],
        [data-popover][data-theme],
        [role="dialog"][data-popover],
        [id^="popover"] {
          z-index: 1500 !important;
        }
        .chakra-portal > div[data-popover] {
          z-index: 1500 !important;
        }
      `;
      document.head.appendChild(style);
      
      // Update immediately and after a short delay to catch dynamically created portals
      updateZIndex();
      const timeout = setTimeout(updateZIndex, 100);
      
      return () => {
        clearTimeout(timeout);
        const existingStyle = document.getElementById('messages-popover-zindex-fix');
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, [isOpen]);

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnBlur={false}
      isLazy
    >
      <PopoverTrigger>{childrenWithBadge}</PopoverTrigger>
      <PopoverContent
        w="900px"
        maxW="90vw"
        h="600px"
        maxH="85vh"
        bg={bgColor}
        boxShadow="xl"
        zIndex={1500}
        style={{ zIndex: 1500, position: 'relative' }}
        className="messages-popover-content"
      >
        <PopoverArrow />
        <PopoverBody p={0} h="100%" overflow="hidden">
          <Flex h="100%" direction="row">
            {/* Sidebar */}
            <Box
              w="320px"
              h="100%"
              borderRightWidth="1px"
              borderColor={useColorModeValue('gray.200', 'gray.600')}
            >
              <ConversationSidebar
                selectedConversationId={selectedConversationId}
                onSelectConversation={setSelectedConversationId}
                onNewConversation={handleNewConversation}
              />
            </Box>

            {/* Main Content */}
            <Box flex="1" h="100%" display="flex" flexDirection="column" overflow="hidden">
              {selectedConversationId ? (
                <>
                  <ChatHeader
                    conversationName={getConversationName()}
                    isGroupChat={selectedConversation?.type === 'group'}
                    memberCount={selectedConversation?.members.length}
                    members={selectedConversation?.membersDetails}
                    onAddMembers={
                      selectedConversation?.type === 'group'
                        ? () => setShowAddMembersModal(true)
                        : undefined
                    }
                  />
                  <Box flex="1" overflow="hidden">
                    <GroupChat
                      fillContainer={false}
                      collectionRef={collection(
                        db,
                        COLLECTION_NAMES.CONVERSATIONS,
                        selectedConversationId,
                        COLLECTION_NAMES.CHAT_MESSAGES
                      )}
                      collectionName="Conversations"
                      parentID={selectedConversationId}
                      heading={undefined}
                      width="100%"
                      fullPageChat={true}
                      isNotes={false}
                      isNotesForReview={false}
                      setNotesForReviewCount={undefined}
                      setShowNotesForReviewToast={undefined}
                      restrictPosting={false}
                      fullPageNotes={false}
                      parentDocContent={null}
                      isDashboard={false}
                      referencedLoan={undefined}
                      maxMsgs={undefined}
                    />
                  </Box>
                </>
              ) : (
                <Box
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={8}
                >
                  <Box textAlign="center">
                    <Heading size="lg" mb={2} color="gray.600">
                      Select a conversation
                    </Heading>
                    <Text color="gray.500" mb={4}>
                      Choose a conversation from the sidebar or start a new one
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          </Flex>
        </PopoverBody>

        {/* Modals */}
        <NewConversationModal
          isOpen={showNewConversationModal}
          onClose={() => setShowNewConversationModal(false)}
          onConversationCreated={handleConversationCreated}
          createDirectMessage={createDirectMessage}
          createGroupChat={createGroupChat}
        />

        {selectedConversation && (
          <AddMembersModal
            isOpen={showAddMembersModal}
            onClose={() => setShowAddMembersModal(false)}
            onMembersAdded={handleMembersAdded}
            conversationId={selectedConversation.id}
            currentMemberIds={selectedConversation.members}
            addMembersToConversation={addMembersToConversation}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

